import {
  AddressState,
  FrameworkContractState,
  PersonBasicInsuranceState,
  PersonState,
  PersonSupplementaryInsuranceState,
} from './offer.state';
import {
  BasicInsuranceRequest,
  ContractDuration,
  FrameworkContract,
  OfferRequest,
  SupplementaryInsurancesRequest,
} from '../../api';

export const mapToOfferRequest = (
  persons: PersonState[],
  address: AddressState,
  saleOfferBegin: string,
  contractDuration: ContractDuration,
  frameworkContract: FrameworkContractState | undefined,
): OfferRequest => ({
  sale_offer_begin: saleOfferBegin,
  bfs_number: address.bfsNumber,
  zip_code: address.zipCode,
  person_offer_requests: persons.map((person) => ({
    products_request: {
      basic_insurance: mapBasicInsurance(person.basicInsurance),
      supplementary_insurances: mapSupplementaryInsurance(person.supplementaryInsurances),
    },
    date_of_birth: person.details.dateOfBirth,
    gender_or_prenatal: person.details.genderOrPrenatal,
  })),
  contract_duration: contractDuration,
  framework_contract: mapToFrameworkContract(frameworkContract),
});

const mapToFrameworkContract = (frameworkContract?: FrameworkContractState): FrameworkContract | undefined => {
  return frameworkContract
    ? {
        number: frameworkContract.number,
        name: frameworkContract.name,
        syrius_key: {
          contract_number: frameworkContract.syriusKey.contractNumber,
          person_group_short_description: frameworkContract.syriusKey.personGroupShortDescription,
        },
      }
    : undefined;
};

const mapBasicInsurance = (basicInsurance?: PersonBasicInsuranceState): BasicInsuranceRequest | null => {
  return basicInsurance
    ? {
        insurer: basicInsurance.key.insurer,
        franchise: basicInsurance.franchise,
        model_type: basicInsurance.key.modelType,
        includes_accident_coverage: basicInsurance.includesAccidentCoverage,
        medic_zsr: basicInsurance.medicalOffice?.id,
        general_practitioner_zsr: basicInsurance.medicalOffice?.general_practitioner_id,
      }
    : null;
};

const mapSupplementaryInsurance = (
  supplementaryInsurance: PersonSupplementaryInsuranceState,
): SupplementaryInsurancesRequest => ({
  basic: supplementaryInsurance.basic
    ? {
        type: supplementaryInsurance.basic.offerType,
        cost_sharing: supplementaryInsurance.basic.costSharing,
        region: supplementaryInsurance.basic.region,
      }
    : undefined,
  hospital: supplementaryInsurance.hospital
    ? {
        type: supplementaryInsurance.hospital.offerType,
        cost_sharing: supplementaryInsurance.hospital.costSharing,
        region: supplementaryInsurance.hospital.region,
      }
    : undefined,
  hospital_viva: supplementaryInsurance.hospitalViva
    ? {
        type: supplementaryInsurance.hospitalViva.offerType,
        cost_sharing: supplementaryInsurance.hospitalViva.costSharing,
        region: supplementaryInsurance.hospitalViva.region,
      }
    : undefined,
  complementary: supplementaryInsurance.complementary
    ? {
        level: supplementaryInsurance.complementary.level,
      }
    : undefined,
  complementary_viva: supplementaryInsurance.complementaryViva
    ? {
        level: supplementaryInsurance.complementaryViva.level,
      }
    : undefined,
  outpatient: supplementaryInsurance.outpatient
    ? {
        level: supplementaryInsurance.outpatient.level,
      }
    : undefined,
  outpatient_viva: supplementaryInsurance.outpatientViva
    ? {
        level: supplementaryInsurance.outpatientViva.level,
      }
    : undefined,
  accident_disability_capital: supplementaryInsurance.accidentDisabilityCapital
    ? {
        accident_benefit: supplementaryInsurance.accidentDisabilityCapital.accidentBenefit,
      }
    : undefined,
  daily_cash: supplementaryInsurance.dailyCash
    ? {
        accident_coverage: supplementaryInsurance.dailyCash.accidentCoverage,
        capital: supplementaryInsurance.dailyCash.capital,
        waiting_period: supplementaryInsurance.dailyCash.waitingPeriod,
      }
    : undefined,
  accident_death_capital: supplementaryInsurance.accidentDeathCapital
    ? {
        accident_benefit: supplementaryInsurance.accidentDeathCapital.accidentBenefit,
      }
    : undefined,
  sickness_death_or_disability_capital: supplementaryInsurance.sicknessDeathOrDisabilityCapital
    ? {
        death_benefit: supplementaryInsurance.sicknessDeathOrDisabilityCapital.deathBenefit,
        disability_benefit: supplementaryInsurance.sicknessDeathOrDisabilityCapital.disabilityBenefit,
      }
    : undefined,
  dental: supplementaryInsurance.dental ? { level: supplementaryInsurance.dental.level } : undefined,
  hospital_capital: supplementaryInsurance.hospitalCapital
    ? {
        capital_hospital: supplementaryInsurance.hospitalCapital.capital,
        accident_coverage: supplementaryInsurance.hospitalCapital.accidentCoverage,
      }
    : undefined,
  hospital_daily_cash: supplementaryInsurance.hospitalDailyCash
    ? {
        capital: supplementaryInsurance.hospitalDailyCash.capital,
        waiting_period: supplementaryInsurance.hospitalDailyCash.waitingPeriod,
        benefit_duration: supplementaryInsurance.hospitalDailyCash.benefitDuration,
      }
    : undefined,
  legal_protection: !!supplementaryInsurance.legalProtection,
  outpatient_comfort: !!supplementaryInsurance.outpatientComfort,
  emergency_care: !!supplementaryInsurance.emergencyCare,
  accident_medical_expenses_supplement: !!supplementaryInsurance.accidentMedicalExpensesSupplement,
});
